/**
 * @description Schedule tab - default view after login
 *
 * Key features:
 * - Clock In/Out + Break buttons at top with current status
 * - Running time display when clocked in
 * - Upcoming shifts list (schedule category events; includes in-progress)
 * - Countdown to next shift
 * - Break usage tracking
 * - Punch errors and policy warnings surfaced to the employee
 *
 * @dependencies
 * - ClockContext: Status, punch actions
 * - clockService: Schedule data
 */

import React, { useState, useEffect } from 'react';
import { Play, Square, Coffee, Clock, MapPin } from 'lucide-react';
import { useClockContext } from '../../contexts/ClockContext';
import clockService from '../../services/clockService';
import StatusBadge from '../shared/StatusBadge';
import TimeDisplay from '../shared/TimeDisplay';
import { formatShiftTime, getCountdown } from '../../utils/timeFormatters';

/**
 * Calculate total break minutes and count from breaks array
 */
const getBreakSummary = (breaks) => {
  if (!breaks || breaks.length === 0) return null;

  let totalMinutes = 0;
  let totalCount = 0;
  let hasPaid = false;

  breaks.forEach(brk => {
    totalMinutes += (brk.durationMinutes || 0) * (brk.countAllowed || 1);
    totalCount += brk.countAllowed || 1;
    if (brk.isPaid) hasPaid = true;
  });

  return { totalMinutes, totalCount, hasPaid };
};

/**
 * Check if a shift is currently active for the open punch.
 * When the server reports punch↔shift linking (linkedShift / unscheduled),
 * trust that — do not fall back to a time-window heuristic that can mark a
 * nearby shift Active while the punch badge correctly says Unscheduled.
 */
const isShiftActive = (shift, clockStatus) => {
  if (!clockStatus?.activeEntry || clockStatus.status === 'clocked_out') return false;

  // Server-linked punch: only the linked shift is Active
  if (clockStatus.linkedShift?.id) {
    return shift.id === clockStatus.linkedShift.id;
  }

  // Explicit unscheduled punch: no shift is Active
  if (clockStatus.unscheduled) {
    return false;
  }

  // Legacy clients without linking fields: time-window heuristic
  const clockInTime = new Date(clockStatus.activeEntry.clockInAt);
  const shiftStart = new Date(shift.startTime || shift.start_time);
  const shiftEnd = new Date(shift.endTime || shift.end_time);

  const earlyBuffer = 30 * 60 * 1000;
  return clockInTime >= new Date(shiftStart.getTime() - earlyBuffer) && clockInTime <= shiftEnd;
};

const ScheduleScreen = () => {
  const { companyId, clockStatus, punch } = useClockContext();
  const [shifts, setShifts] = useState([]);
  const [ptoBlocks, setPtoBlocks] = useState([]);
  const [punching, setPunching] = useState(false);
  const [punchError, setPunchError] = useState(null);
  const [policyWarning, setPolicyWarning] = useState(null);

  useEffect(() => {
    loadSchedule();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSchedule = async () => {
    try {
      const [data, ptoRequests] = await Promise.all([
        clockService.getSchedule(companyId),
        clockService.getPtoRequests(companyId).catch(() => []),
      ]);
      setShifts(data);
      const list = Array.isArray(ptoRequests) ? ptoRequests : (ptoRequests.requests || []);
      setPtoBlocks(list.filter((r) => ['approved', 'taken'].includes(r.status)));
    } catch (err) {
      setShifts([]);
      setPtoBlocks([]);
    }
  };

  const handlePunch = async (action) => {
    setPunching(true);
    setPunchError(null);
    setPolicyWarning(null);
    const outcome = await punch(action);
    if (!outcome?.success) {
      setPunchError(outcome?.error || 'Punch failed');
    } else if (outcome?.result?.policyWarning) {
      setPolicyWarning(outcome.result.policyWarning);
    }
    // Refresh schedule after punch so Active badge stays accurate
    await loadSchedule();
    setPunching(false);
  };

  const status = clockStatus?.status || 'clocked_out';
  const activeEntry = clockStatus?.activeEntry;
  const isUnscheduled = status !== 'clocked_out' && clockStatus?.unscheduled;

  // Find next upcoming shift
  const now = new Date();
  const nextShift = shifts.find(s => new Date(s.startTime || s.start_time) > now);
  const countdown = nextShift ? getCountdown(nextShift.startTime || nextShift.start_time) : null;

  return (
    <div className="p-4 space-y-6">
      {/* Clock Status + Action Buttons */}
      <div className="bg-slate-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={status} />
            {status !== 'clocked_out' && (
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                isUnscheduled
                  ? 'bg-amber-500/20 text-amber-300'
                  : 'bg-primary-500/20 text-primary-400'
              }`}>
                {isUnscheduled ? 'Unscheduled' : 'Scheduled'}
              </span>
            )}
          </div>
          {status === 'clocked_in' && activeEntry && (
            <TimeDisplay startTime={activeEntry.clockInAt} label="Elapsed" />
          )}
        </div>

        {punchError && (
          <div className="mb-3 rounded-lg bg-red-500/15 border border-red-500/30 px-3 py-2 text-sm text-red-300">
            {punchError}
          </div>
        )}
        {policyWarning && !punchError && (
          <div className="mb-3 rounded-lg bg-amber-500/15 border border-amber-500/30 px-3 py-2 text-sm text-amber-200">
            {policyWarning}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {status === 'clocked_out' && (
            <button
              onClick={() => handlePunch('clock_in')}
              disabled={punching}
              className="flex-1 flex items-center justify-center gap-2 py-4 bg-green-600 hover:bg-green-500 active:bg-green-400 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 min-h-touch"
            >
              <Play className="w-5 h-5" />
              Clock In
            </button>
          )}

          {status === 'clocked_in' && (
            <>
              <button
                onClick={() => handlePunch('break_start')}
                disabled={punching}
                className="flex-1 flex items-center justify-center gap-2 py-4 bg-yellow-600 hover:bg-yellow-500 active:bg-yellow-400 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 min-h-touch"
              >
                <Coffee className="w-5 h-5" />
                Break
              </button>
              <button
                onClick={() => handlePunch('clock_out')}
                disabled={punching}
                className="flex-1 flex items-center justify-center gap-2 py-4 bg-red-600 hover:bg-red-500 active:bg-red-400 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 min-h-touch"
              >
                <Square className="w-5 h-5" />
                Clock Out
              </button>
            </>
          )}

          {status === 'on_break' && (
            <button
              onClick={() => handlePunch('break_end')}
              disabled={punching}
              className="flex-1 flex items-center justify-center gap-2 py-4 bg-primary-600 hover:bg-primary-500 active:bg-primary-400 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 min-h-touch"
            >
              <Play className="w-5 h-5" />
              End Break
            </button>
          )}
        </div>
      </div>

      {/* Next Shift Countdown */}
      {countdown && (
        <div className="bg-slate-800/50 rounded-xl p-4 flex items-center gap-3">
          <Clock className="w-5 h-5 text-primary-400 flex-shrink-0" />
          <div>
            <p className="text-sm text-slate-400">Next shift</p>
            <p className="text-white font-medium">{countdown}</p>
          </div>
        </div>
      )}

      {/* Approved PTO overlay */}
      {ptoBlocks.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3">
            Time Off
          </h3>
          <div className="space-y-2 mb-4">
            {ptoBlocks.slice(0, 5).map((pto) => (
              <div
                key={pto.id}
                className="bg-sky-950/40 border border-sky-500/30 rounded-xl p-4"
              >
                <p className="text-sky-200 font-medium capitalize">
                  {(pto.leaveType || pto.leave_type || 'time off').replace('_', ' ')}
                </p>
                <p className="text-sm text-slate-400">
                  {String(pto.startDate || pto.start_date).slice(0, 10)}
                  {(pto.endDate || pto.end_date) &&
                    ` → ${String(pto.endDate || pto.end_date).slice(0, 10)}`}
                  {(pto.hoursRequested || pto.hours_requested) != null &&
                    ` · ${pto.hoursRequested || pto.hours_requested}h`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Shifts */}
      <div>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3">
          Upcoming Shifts
        </h3>

        {shifts.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No upcoming shifts scheduled</p>
          </div>
        ) : (
          <div className="space-y-2">
            {shifts.slice(0, 7).map((shift, i) => {
              const breaks = shift.allowedBreaks || shift.allowed_breaks;
              const breakSummary = getBreakSummary(breaks);
              const startTime = shift.startTime || shift.start_time;
              const endTime = shift.endTime || shift.end_time;

              const isActive = isShiftActive(shift, clockStatus);
              const breakMinutesUsed = isActive ? (clockStatus?.breakMinutesUsed || 0) : 0;
              const isOverBreak = breakSummary && breakMinutesUsed > breakSummary.totalMinutes;

              return (
                <div
                  key={shift.id || i}
                  className={`bg-slate-800 rounded-xl p-4 ${isActive ? 'ring-2 ring-primary-500/50' : ''}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">
                          {formatShiftTime(startTime, endTime)}
                        </p>
                        {isActive && (
                          <span className="text-xs px-1.5 py-0.5 bg-primary-500/20 text-primary-400 rounded font-medium">
                            Active
                          </span>
                        )}
                      </div>
                      {(shift.locationName || shift.location_name) && (
                        <p className="text-sm text-slate-400 truncate flex items-center gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          {shift.locationName || shift.location_name}
                          {(shift.locationState || shift.location_state) && (
                            <span className="text-slate-500">({shift.locationState || shift.location_state})</span>
                          )}
                        </p>
                      )}
                      {shift.title && !shift.locationName && !shift.location_name && (
                        <p className="text-sm text-slate-400 truncate">{shift.title}</p>
                      )}
                    </div>

                    {breakSummary && (
                      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg flex-shrink-0 ${
                        isOverBreak ? 'bg-red-500/20' : 'bg-slate-700/50'
                      }`}>
                        <Coffee className={`w-3.5 h-3.5 ${isOverBreak ? 'text-red-400' : 'text-yellow-400'}`} />
                        {isActive ? (
                          <span className={`text-xs font-medium ${isOverBreak ? 'text-red-400' : 'text-slate-300'}`}>
                            {breakMinutesUsed}/{breakSummary.totalMinutes}m
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300 font-medium">
                            {breakSummary.totalMinutes}m
                          </span>
                        )}
                        {breakSummary.hasPaid && (
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400" title="Includes paid breaks" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleScreen;
